import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  Zap, Shield, Clock, Headphones, Server, Globe,
  Terminal, Archive, Users, Settings, CreditCard,
  BarChart2, Lock, RefreshCw, ChevronRight
} from 'lucide-react';

const FEATURES = [
  {
    category: 'Performance',
    items: [
      { icon: Zap, title: 'Instant Deployment', desc: 'Your Minecraft server is live within 30 seconds of payment. No waiting, no manual steps, fully automated.' },
      { icon: Server, title: 'NVMe SSD Storage', desc: 'All servers run on NVMe SSD drives for fast chunk loading and world saves. Night and day vs HDD hosting.' },
      { icon: Globe, title: 'Low Latency Nodes', desc: 'Servers optimized for low ping. Your players get smooth gameplay from anywhere in Europe.' },
      { icon: RefreshCw, title: 'Automatic Restarts', desc: 'If your server crashes, it automatically restarts within seconds. Maximum uptime guaranteed.' },
    ]
  },
  {
    category: 'Control',
    items: [
      { icon: Terminal, title: 'Live Console', desc: 'Full browser-based console. Run commands, view logs, debug issues — all in real time without any app.' },
      { icon: Settings, title: 'File Manager', desc: 'Browse, edit, upload and delete server files right in your browser. No FTP client needed.' },
      { icon: Archive, title: 'Automated Backups', desc: 'Daily backups kept for 7 days. One-click restore gets your world back instantly if anything goes wrong.' },
      { icon: Users, title: 'Player Management', desc: 'Manage your whitelist, operators and banned players with a simple interface. No commands needed.' },
    ]
  },
  {
    category: 'Security',
    items: [
      { icon: Shield, title: 'DDoS Protection', desc: 'Enterprise-grade DDoS mitigation keeps your server online during attacks. Play without interruptions.' },
      { icon: Lock, title: 'Isolated Servers', desc: 'Each server runs in its own isolated container. One server having issues never affects another.' },
      { icon: Clock, title: '99.9% Uptime SLA', desc: 'We guarantee 99.9% uptime on all plans. If we miss it, you get credit on your next invoice.' },
      { icon: RefreshCw, title: 'Automatic Updates', desc: 'Keep your server on the latest Paper/Vanilla/Forge build with one click from your dashboard.' },
    ]
  },
  {
    category: 'Support',
    items: [
      { icon: Headphones, title: '24/7 Support', desc: 'Real humans available around the clock via our ticket system and Discord community.' },
      { icon: BarChart2, title: 'Resource Monitoring', desc: 'See CPU, RAM and disk usage for your server in real time from your dashboard.' },
      { icon: CreditCard, title: 'Simple Billing', desc: 'Monthly, quarterly or annual billing. Upgrade or cancel anytime. Full invoice history always available.' },
      { icon: Globe, title: 'All Major Versions', desc: 'Every Minecraft version from 1.8 to the latest release. Paper, Vanilla, Forge and Fabric all supported.' },
    ]
  },
];

const FeaturesPage = () => {
  const { user } = useAuth();
  return (
  <div className="min-h-screen bg-gray-950 pt-24 pb-16 px-4">
    <div className="max-w-6xl mx-auto">

      {/* Hero */}
      <div className="text-center mb-20">
        <h1 className="text-5xl font-black text-white mb-4">
          Everything you need to run<br />
          <span className="text-green-400">a great Minecraft server</span>
        </h1>
        <p className="text-gray-400 text-xl max-w-2xl mx-auto mb-8">
          Built for server owners who want performance, control and reliability — without the complexity.
        </p>
        <Link
          to="/pricing"
          className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-8 py-4 rounded-xl text-lg transition-all hover:shadow-lg hover:shadow-green-500/30"
        >
          View Plans <ChevronRight className="w-5 h-5" />
        </Link>
      </div>

      {/* Feature sections */}
      {FEATURES.map(({ category, items }) => (
        <div key={category} className="mb-16">
          <h2 className="text-xs font-bold text-green-400 uppercase tracking-widest mb-6">
            {category}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {items.map(({ icon: Icon, title, desc }) => (
              <div
                key={title}
                className="bg-gray-900 border border-gray-800 hover:border-green-500/30 rounded-2xl p-6 flex gap-4 transition-colors group"
              >
                <div className="w-11 h-11 bg-green-500/10 group-hover:bg-green-500/20 rounded-xl flex items-center justify-center flex-shrink-0 transition-colors">
                  <Icon className="w-5 h-5 text-green-400" />
                </div>
                <div>
                  <h3 className="font-bold text-white mb-1.5">{title}</h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Supported versions */}
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-8 mb-16">
        <h2 className="text-2xl font-black text-white mb-2">Supported server types</h2>
        <p className="text-gray-400 mb-6">Choose the software that fits your community best.</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { name: 'Paper', desc: 'Best performance + plugin support. Recommended for most servers.', badge: 'Recommended' },
            { name: 'Vanilla', desc: 'Pure Minecraft experience, exactly as Mojang intended.', badge: null },
            { name: 'Forge', desc: 'Full mod support for heavily modded servers and modpacks.', badge: null },
            { name: 'Fabric', desc: 'Lightweight modern mod loader, fast and efficient.', badge: null },
          ].map(({ name, desc, badge }) => (
            <div key={name} className="bg-gray-800/50 rounded-xl p-4 relative">
              {badge && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-gray-950 text-xs font-bold px-2 py-0.5 rounded-full">
                  {badge}
                </span>
              )}
              <div className="font-bold text-white mb-1">{name}</div>
              <div className="text-gray-500 text-xs leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="text-center bg-gradient-to-b from-green-500/5 to-transparent border border-green-500/10 rounded-2xl py-16 px-4">
        <h2 className="text-3xl font-black text-white mb-3">Ready to get started?</h2>
        <p className="text-gray-400 mb-8">Deploy your first server in under 30 seconds.</p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={user ? '/pricing' : '/register'}
            className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-400 text-gray-950 font-bold px-8 py-4 rounded-xl transition-all hover:shadow-lg hover:shadow-green-500/30"
          >
            {user ? 'Browse Plans' : 'Create free account'} <ChevronRight className="w-5 h-5" />
          </Link>
          {!user && (
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 bg-gray-800 hover:bg-gray-700 text-white px-8 py-4 rounded-xl transition-colors"
            >
              See pricing
            </Link>
          )}
        </div>
      </div>
    </div>
  </div>
  );
};

export default FeaturesPage;
